/* eslint-disable react/prop-types */
/**
 * AgentDebugPanel — uniwersalny panel diagnostyczny dla agentów AI.
 *
 * Renderuje pełny `debug_info` ostatniej analizy: skrót + 9 rozwijanych sekcji.
 * Każda sekcja ma przycisk "Kopiuj" (z fallbackiem do execCommand i toast).
 *
 * Używany w:
 *  - PaymentPackagesPage (Tab Kontrahenci) — Agent Płatności
 *  - InvoiceAgentTab — Agent Faktur
 *  - BankAgentTab — Agent Wyciągów
 *
 * Panel:
 *  - domyślnie zwinięty,
 *  - widoczny tylko gdy `debugInfo` jest niepuste,
 *  - mobile-first (monospace, max-h, whitespace-pre-wrap break-all),
 *  - bez poziomego overflow.
 */
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Bug, Sparkles, ChevronDown, ChevronUp, Copy,
} from "lucide-react";

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      toast.success("Skopiowano do schowka");
      return;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) toast.success("Skopiowano do schowka");
    else toast.error("Nie udało się skopiować — brak dostępu do schowka.");
  } catch {
    toast.error("Nie udało się skopiować — brak dostępu do schowka.");
  }
};

const stringifyForCopy = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "string") return val;
  try {
    return JSON.stringify(val, null, 2);
  } catch {
    return String(val);
  }
};

const DebugSection = ({ title, content, testId, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const text = stringifyForCopy(content);
  const empty = !text || text === "{}" || text === "[]" || text === '""';
  return (
    <div className="border border-gray-200 rounded-md bg-white" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/60">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-gray-900"
          data-testid={`${testId}-toggle`}
        >
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {title}
          {empty ? <span className="text-[10px] text-gray-400 ml-1">(pusto)</span> : null}
        </button>
        <button
          type="button"
          onClick={() => copyToClipboard(text)}
          className="inline-flex items-center gap-1 text-[11px] text-gray-600 hover:text-indigo-700 px-2 py-1 rounded border border-gray-200 bg-white hover:bg-indigo-50"
          data-testid={`${testId}-copy`}
          title="Kopiuj do schowka"
        >
          <Copy className="w-3 h-3" /> Kopiuj
        </button>
      </div>
      {open ? (
        <pre
          className="text-[11px] font-mono text-gray-800 px-3 py-2 max-h-72 overflow-auto whitespace-pre-wrap break-all bg-white"
          data-testid={`${testId}-body`}
        >
{text || "(pusto)"}
        </pre>
      ) : null}
    </div>
  );
};

/**
 * Renderuje wiersz "label : value" w siatce skrótu.
 */
const SummaryCell = ({ label, value, testId, accent }) => (
  <div className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100 min-w-0">
    <div className="text-[10px] text-gray-500">{label}</div>
    <div
      className={`font-medium text-[12px] break-words ${accent || "text-gray-900"}`}
      data-testid={testId}
    >
      {value === null || value === undefined || value === "" ? "—" : String(value)}
    </div>
  </div>
);

/**
 * AgentDebugPanel — główny komponent.
 *
 * Props:
 *  - title: tytuł panelu (np. "Log ostatniej analizy Agenta Faktur")
 *  - debugInfo: pełen obiekt debug_info z backendu
 *  - summaryRows: opcjonalnie tablica { label, value, accent } z dodatkowymi
 *    polami w skrócie (dla konkretnego agenta, np. NIP / payment_method / IBAN)
 *  - testIdPrefix: prefix dla data-testid (np. "invoice-debug" / "bank-debug")
 */
const AgentDebugPanel = ({ title, debugInfo, summaryRows, testIdPrefix }) => {
  const [open, setOpen] = useState(false);

  if (!debugInfo) return null;
  const prefix = testIdPrefix || "agent-debug";

  const ts = debugInfo.timestamp ? new Date(debugInfo.timestamp).toLocaleString("pl-PL") : "—";
  const entityLine = [
    debugInfo.entity_name || debugInfo.vendor_name || "",
    debugInfo.entity_id || debugInfo.vendor_nip || "",
  ].filter(Boolean).join(" · ");

  return (
    <Card className="border-gray-200 bg-white" data-testid={`${prefix}-panel`}>
      <CardContent className="p-3 sm:p-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 text-left"
          data-testid={`${prefix}-toggle`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 rounded-md bg-gray-100 shrink-0">
              <Bug className="w-4 h-4 text-gray-700" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                {title}
                {debugInfo.llm_used ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-violet-300 bg-violet-50 text-violet-700 inline-flex items-center gap-0.5">
                    <Sparkles className="w-3 h-3" /> AI
                  </span>
                ) : null}
              </div>
              <div className="text-[11px] text-gray-500 truncate">
                {entityLine || "—"} · {ts}
              </div>
            </div>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />}
        </button>

        {open ? (
          <div className="mt-3 space-y-3" data-testid={`${prefix}-body`}>
            {/* 1. Skrót */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <SummaryCell
                label="Provider / model"
                value={`${debugInfo.provider || "—"} / ${debugInfo.model || "—"}`}
                testId={`${prefix}-provider`}
              />
              <SummaryCell
                label="Czas wykonania"
                value={`${debugInfo.execution_time_ms ?? 0} ms`}
                testId={`${prefix}-time`}
              />
              <SummaryCell
                label="LLM użyty"
                value={debugInfo.llm_used ? "tak" : "nie"}
                accent={debugInfo.llm_used ? "text-emerald-700" : "text-gray-500"}
                testId={`${prefix}-llm-used`}
              />
              <SummaryCell
                label="Fallback"
                value={debugInfo.fallback_used ? "tak" : "nie"}
                accent={debugInfo.fallback_used ? "text-amber-700" : "text-gray-500"}
                testId={`${prefix}-fallback`}
              />
              {/* Dodatkowe pola per-agent */}
              {(summaryRows || []).map((row, i) => (
                <SummaryCell
                  key={i}
                  label={row.label}
                  value={row.value}
                  accent={row.accent}
                  testId={`${prefix}-summary-${i}`}
                />
              ))}
              <SummaryCell
                label="Błąd parsowania"
                value={debugInfo.parse_error || "—"}
                accent={debugInfo.parse_error ? "text-rose-700" : "text-gray-500"}
                testId={`${prefix}-parse-error`}
              />
              <SummaryCell
                label="Błąd LLM"
                value={debugInfo.llm_error || "—"}
                accent={debugInfo.llm_error ? "text-rose-700" : "text-gray-500"}
                testId={`${prefix}-llm-error`}
              />
            </div>

            {/* Notatki diagnostyczne (np. "IBAN znaleziony / nieznaleziony") */}
            {Array.isArray(debugInfo.notes) && debugInfo.notes.length > 0 ? (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-[11px] text-blue-900">
                <div className="font-semibold mb-1">Notatki diagnostyczne:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {debugInfo.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            ) : null}

            {/* 2-9. Rozwijane sekcje */}
            <div className="space-y-2">
              <DebugSection title="Dane wejściowe" content={debugInfo.input_payload} testId={`${prefix}-input`} />
              <DebugSection title="Agregacja" content={debugInfo.aggregation} testId={`${prefix}-aggregation`} />
              <DebugSection title="Prompt systemowy" content={debugInfo.prompt_system} testId={`${prefix}-prompt-system`} />
              <DebugSection title="Prompt użytkownika" content={debugInfo.prompt_user} testId={`${prefix}-prompt-user`} />
              <DebugSection title="Surowa odpowiedź LLM" content={debugInfo.raw_response} testId={`${prefix}-raw`} />
              <DebugSection title="Sparsowany JSON" content={debugInfo.parsed_json} testId={`${prefix}-parsed`} />
              <DebugSection title="Dane zapisane do profilu" content={debugInfo.saved_profile} testId={`${prefix}-saved`} />
              <DebugSection title="Cały log" content={debugInfo} testId={`${prefix}-full`} />
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default AgentDebugPanel;
